import React, { useState, useEffect, useRef } from 'react';
import { trackLandingCTA, trackSignIn } from '@/lib/analytics';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui/button';

export interface NavProps {
  onSignIn?: () => void;
  onSignUp?: (tier?: string) => void;
  onGoToDashboard?: () => void;
  isSignedIn?: boolean;
}

// Marketing ruling M1 (2026-10-20): in-page anchors only. The /docs and /faq
// links come back once those pages have been audited for retired features
// (M1 delete list, item 62).
const navLinks = [
  { label: 'How it works', href: '#how-it-works' },
  { label: 'What it holds', href: '#what-it-holds' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Is this for you?', href: '#is-this-for-you' },
];

export const Nav: React.FC<NavProps> = ({
  onSignIn,
  onSignUp,
  onGoToDashboard,
  isSignedIn = false,
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on outside click
  useEffect(() => {
    if (!mobileOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [mobileOpen]);

  const linkClass = 'text-sm text-white/70 hover:text-white transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2dd4a0] rounded-sm';

  return (
    <div ref={mobileRef}>
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-[1000] px-6 py-4 transition-all duration-300 ${
          scrolled || mobileOpen
            ? 'glass shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1280px] mx-auto flex justify-between items-center">
          <Logo variant="full" iconSize={28} />

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a key={link.label} href={link.href} className={linkClass}>{link.label}</a>
            ))}
          </div>

          <div className="flex gap-3 items-center">
            {isSignedIn ? (
              <Button onClick={onGoToDashboard} size="sm">
                Dashboard
                <span className="ml-1">→</span>
              </Button>
            ) : (
              <>
                <button onClick={() => { trackSignIn(); onSignIn?.(); }} className={`hidden sm:inline-flex px-4 py-2 ${linkClass}`}>
                  Sign in
                </button>
                <Button onClick={() => { trackLandingCTA('nav'); onSignUp?.('observer'); }} size="sm">
                  Open a paper account
                </Button>
              </>
            )}
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden ml-1 p-2 text-white/70 hover:text-white transition-colors"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile dropdown */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="fixed top-[64px] left-0 right-0 z-[999] glass shadow-lg border-t border-white/[0.06] md:hidden"
          >
            <div className="max-w-[1280px] mx-auto px-6 py-4 flex flex-col gap-3">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block py-2 text-base text-white/70 hover:text-white transition-colors"
                >
                  {link.label}
                </a>
              ))}
              {!isSignedIn && (
                <button
                  onClick={() => { setMobileOpen(false); onSignIn?.(); }}
                  className="sm:hidden block py-2 text-base text-white/70 hover:text-white transition-colors text-left"
                >
                  Sign in
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
